<template>
  <div class="table-component">
    <div class="table-wrapper relative">
      <div v-if="loading" class="table-loader">
        <div class="spinner"></div>
      </div>

      <table :class="tableClass">
        <thead>
          <!-- Table Header -->
          <tr>
            <th v-for="column in columns" :key="JSON.stringify(column)"
              @click="column.sortable ? sortTable(column.key) : null"
              :style="getHeaderStyle(column)">
              {{ column.label }}
              <span v-if="column.sortable" style="position: absolute; width: 20px; text-align: center">
                <i v-if="sortKey === column.key"
                  :class="sortOrder === 'asc' ? 'now-ui-icons arrows-1_minimal-up' : 'now-ui-icons arrows-1_minimal-down'"></i>
                <i v-else class="now-ui-icons arrows-1_minimal-up" style="visibility: hidden"></i>
              </span>
            </th>
          </tr>
        </thead>
        <tbody class="table-scroll">
        <tr
          v-for="item in paginatedData"
          :key="JSON.stringify(item)"
          class="table-row"
          @mouseover="hoveredRow = item"
          @mouseleave="hoveredRow = null"
        >
          <td
            v-for="column in columns"
            :key="JSON.stringify(column)"
              :class="['table-cell-td', { 'sticky-action': column.sticky }]" 
              :style="getCellStyle(column, item)">
              <slot :name="column.key" :item="item">
                {{ item[column.key] }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <PaginationComponent :currentPage="currentPage" :totalPages="totalPages" :visiblePages="visiblePages" :itemsPerPage="itemsPerPage"
      :pageOptions="pageOptions" @change-page="changePage" @change-items-per-page="changeItemsPerPage" />
  </div>
</template>

<script>
import PaginationComponent from "./PaginationComponent.vue";
export default {
  components: {
    PaginationComponent,
  },
  props: {
    columns: Array,
    data: Array,
    defaultSortKey: {
      type: String,
      default: ""
    },
    defaultSortOrder: {
      type: String,
      default: "asc"
    },
    itemsPerPageOptions: {
      type: Array,
      default: () => [10, 15, 20],
    },
    loading: {
      type: Boolean,
      default: false
    },
    footer: {
      type: Boolean,
      default: false
    },
    totals: {
      type: Object,
      default: () => ({ reopened: 0, deadlineMissed: 0, score: 0 })
    },
    resetPage: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentPage: 1,
      sortKey: "",
      sortOrder: "", // Default to no sorting
      itemsPerPage: 20,
      pageOptions: [5, 10, 15, 20],
      hoveredRow: null,
      maxVisiblePages: 7,
    };
  },
  mounted() {
    window.addEventListener("resize", () => this.$forceUpdate());
  },
  beforeDestroy() {
    window.removeEventListener("resize", () => this.$forceUpdate());
  },
  computed: {
    isMobile() {
      return window.innerWidth <= 768;
    },
    totalPages() {
      return Math.ceil(this.data.length / this.itemsPerPage);
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const max = this.maxVisiblePages;

      if (total <= max) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      const half = Math.floor(max / 2);
      let start = current - half;
      let end = current + half;

      if (start < 1) {
        start = 1;
        end = max;
      }

      if (end > total) {
        end = total;
        start = total - max + 1;
      }

      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
    sortedData() {
      let sorted = Array.isArray(this.data) ? [...this.data] : [];

      if (this.sortKey) {
        if (this.sortKey === 'dates') {
          const withSortValues = sorted.map(item => {
            const dateStr = item[this.sortKey];
            return {
              original: item,
              sortValue: this.getDateSortValue(dateStr)
            };
          });
          withSortValues.sort((a, b) => {
            const modifier = this.sortOrder === "asc" ? 1 : -1;
            return (a.sortValue - b.sortValue) * modifier;
          });
          sorted = withSortValues.map(item => item.original);
        } else {
          sorted.sort((a, b) => {
            const modifier = this.sortOrder === "asc" ? 1 : -1;
            if (a[this.sortKey] < b[this.sortKey]) return -1 * modifier;
            if (a[this.sortKey] > b[this.sortKey]) return 1 * modifier;
            return 0;
          });
        }
      } else {
        sorted.sort((a, b) => b.createdAt - a.createdAt);
      }

      return sorted;
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.sortedData.slice(start, end);
    },
    tableClass() {
      const user = this.$store.state.auth.user;
      const path = this.$route && this.$route.path ? this.$route.path : '';
      if (path === '/usermanagement' && user.role=="admin") {
        return 'table-usermanagement';
      }
      if (path === '/performance' && user.role=="user") {
        return 'table-performance-employee';
      }
      if (path === '/usermanagement' && user.role=="user") {
        return 'table-usermanagement-employee';
      }
      if (path === '/chatbot') {
        return 'table-chatbotmanagement';
      }
      if (path === '/settings') {
        return 'table-chatbotmanagement';
      }
      if (path === '/activity-logs') {
        return 'admin-log';
      }
      if (path === '/reported-bugs') {
        return 'reported-bugs-table';
      }
      return user.role === 'admin' ? 'table-admin' : 'table-employee';
    },
  },
  methods: {
    isStickyDisabledOnMobile(column) {
      const keys = ["profile_picture", "assignees", "assigneeName", "action", "status", "activityOnUserName", "description"];
      return this.isMobile && keys.includes(column.key);
    },
    getStickyOffset(column, position) {
      let offset = 0;

      const ordered =
        position === "left" ? this.columns : [...this.columns].reverse();

      for (let col of ordered) {
        if (col === column) break;

        const isDisabledMobile = this.isStickyDisabledOnMobile(col);

        if (col.sticky === position && !isDisabledMobile) {
          offset += col.width || 150;
        }
      }

      return offset + "px";
    },
    getHeaderStyle(column) {
      const disableStickyMobile = this.isStickyDisabledOnMobile(column);

      const style = {
        position: column.sticky && !disableStickyMobile ? "sticky" : "static",
        zIndex: column.sticky && !disableStickyMobile ? 2 : 1,
        background: column.sticky && !disableStickyMobile ? "#435851" : "inherit",
        color: "white",
        textAlign: "center",
        cursor: column.sortable ? "pointer" : "default",
        width: column.width ? column.width + "px" : "auto",
      };

      if (!disableStickyMobile) {
        if (column.sticky === "left") {
          style.left = this.getStickyOffset(column, "left");
        }
        if (column.sticky === "right") {
          style.right = this.getStickyOffset(column, "right");
        }
      }

      return style;
    },
    getCellStyle(column, item) {
      const disableStickyMobile = this.isStickyDisabledOnMobile(column);

      const style = {
        position: column.sticky && !disableStickyMobile ? "sticky" : "static",
        zIndex: column.sticky && !disableStickyMobile ? 2 : 1,
        background: column.sticky ? "#ffffff" : this.getCellBackground(item, column),
        borderLeft: column.sticky ? '#ccc' : 'inherit',
        width: column.width ? column.width + "px" : "auto",
        maxWidth: column.width ? column.width + "px" : "200px"
      };

      if (!disableStickyMobile) {
        if (column.sticky === "left") {
          style.left = this.getStickyOffset(column, "left");
        }
        if (column.sticky === "right") {
          style.right = this.getStickyOffset(column, "right");
        }
      }

      return style;
    },
    getCellBackground(item, column) {
    if (column.sticky) {
      if (this.hoveredRow === item) {
        return '#f1eded'; 
      }
      return 'inherit';
    }
    },
    getDateSortValue(dateStr) {
      if (!dateStr) return 0;
      
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [, month, day] = dateStr.split('-').map(Number);
        return (month * 100) + day;
      }
      
      try {
        let date = dateStr;
        if (dateStr.includes('-')) {
          date = dateStr.split('-')[0].trim();
        }
        const parts = date.split(' ');
        if (parts.length !== 2) {
          console.warn(`Invalid date format: ${dateStr}`);
          return 0;
        }
        
        const monthStr = parts[0];
        const dayStr = parts[1];
        const monthMap = {
          'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
          'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
        };
        
        if (!monthMap[monthStr]) {
          console.warn(`Invalid month: ${monthStr} in ${dateStr}`);
          return 0;
        }
        const day = parseInt(dayStr, 10);
        if (isNaN(day)) {
          console.warn(`Invalid day: ${dayStr} in ${dateStr}`);
          return 0;
        }
        const sortValue = (monthMap[monthStr] * 100) + day;
        console.log(`Parsed "${dateStr}" as ${sortValue}`);
        return sortValue;
      } catch (error) {
        console.error(`Error parsing date: ${dateStr}`, error);
        return 0;
      }
    },
    changePage(page) {
      if (page > 0 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    sortTable(key) {
      if (this.sortKey === key) {
        if (this.sortOrder === "asc") {
          this.sortOrder = "desc";
        } else if (this.sortOrder === "desc") {
          this.sortOrder = "";
          this.sortKey = "";
        } else {
          this.sortOrder = "asc";
        }
      } else {
        this.sortOrder = "asc";
        this.sortKey = key;
      }
    },
    changeItemsPerPage(newItemsPerPage) {
      this.itemsPerPage = newItemsPerPage;
      this.changePage(1);
    },
  },
  watch: {
    data: {
      immediate: true,
      handler(newValue) {
        if (Array.isArray(newValue) && newValue.length && this.defaultSortKey) {
          this.sortKey = this.defaultSortKey;
          this.sortOrder = this.defaultSortOrder;
        }
      }
    },
    resetPage(newVal) {
      if (newVal) {
        this.currentPage = 1;
        this.$emit('update:resetPage', false);
      }
    }
  }
};
</script>
<style scoped>
.table-usermanagement {
  max-width: auto;
  min-width: 3800px;
  font-size: 13px;
}
 .table-usermanagement-employee {
   max-width: auto;
   min-width: 900px;
   font-size: 13px;
 }
 .table-chatbotmanagement {
   max-width: auto;
   min-width: 900px;
   font-size: 13px;
 }
 .table-holiday-management {
   max-width: auto;
   min-width: 900px;
   font-size: 13px;
 }
 .admin-log {
   max-width: auto;
   min-width: 900px;
   font-size: 13px;
 }
 .reported-bugs-table {
   max-width: auto;
   min-width: 1200px;
   font-size: 13px;
 }
.table-performance-employee {
  min-width: 1400px;
}   
</style>


// WEBPACK FOOTER //
// src/components/UIComponents/CustomTable.vue